"use client";
import {
  FocusEvent,
  ForwardedRef,
  forwardRef,
  memo,
  useCallback,
  useMemo,
  useState,
} from "react";

import { InputProps } from "@nextui-org/input";

import BaseInput, { BaseInputProps } from "@/ui/Forms/BaseInput";

interface FormInputProps extends BaseInputProps {
  autoSelect?: boolean;
}

const FormInputComponent = (
  { autoSelect, type = "text", onFocus, ...props }: FormInputProps & InputProps,
  ref: ForwardedRef<HTMLInputElement>,
) => {
  const [isVisiblePassword, setIsVisiblePassword] = useState(false);

  const handleFocus = useCallback(
    (event: FocusEvent<HTMLInputElement>) => {
      if (autoSelect) {
        event.currentTarget.select();
      }

      if (onFocus) {
        onFocus(event);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [autoSelect], // onFocus is usually stable from parent - excluded for performance
  );

  const togglePasswordVisibility = useCallback(() => {
    setIsVisiblePassword((prev) => !prev);
  }, []);

  const insideRightComponent = useMemo(() => {
    const passwordToggle =
      type === "password" ? (
        <button
          type="button"
          className="form-input-password-toggle"
          onClick={togglePasswordVisibility}
        ></button>
      ) : null;

    if (passwordToggle) {
      return <div className="form-input-end-content">{passwordToggle}</div>;
    }
    return props.insideRightComponent;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type, isVisiblePassword, props.insideRightComponent]); // passwordToggle moved inside useMemo for performance

  return (
    <BaseInput
      ref={ref}
      type={isVisiblePassword ? "text" : type}
      onFocus={handleFocus}
      insideRightComponent={insideRightComponent}
      {...props}
    />
  );
};

const FormInput = memo(forwardRef(FormInputComponent));

// Добавляем displayName для отладки
FormInput.displayName = "FormInput";

export default FormInput;
